// apps/web/src/pages/NoteDetailPage.tsx
// 查看笔记 — 题目结果样式 + 当前笔记

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import QuestionRenderer from "@components/practice/QuestionRenderer";
import { getNoteApi, updateNoteApi } from "@api/userData";
import type { NoteDTO } from "@examfast/shared-types";

export default function NoteDetailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { noteId } = useParams<{ noteId: string }>();
  const [editingNote, setEditingNote] = useState<NoteDTO | null>(null);

  const { data: note, isLoading, isError, error } = useQuery({
    queryKey: ["note", noteId],
    queryFn: () => getNoteApi(noteId!),
    enabled: !!noteId,
  });

  const updateMut = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) => updateNoteApi(id, content),
    onSuccess: (updated) => {
      toast.success("笔记已更新");
      setEditingNote(null);
      queryClient.setQueryData(["note", noteId], updated);
      queryClient.invalidateQueries({ queryKey: ["notes"] });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Header onBack={() => navigate(-1)} />
        <div className="space-y-5 p-4">
          <div className="h-40 rounded-xl bg-gray-100 animate-pulse" />
          <div className="h-24 rounded-xl bg-gray-100 animate-pulse" />
          <div className="h-32 rounded-xl bg-gray-100 animate-pulse" />
        </div>
      </div>
    );
  }

  if (isError || !note) {
    return (
      <div className="min-h-screen bg-white">
        <Header onBack={() => navigate(-1)} />
        <div className="px-8 py-20 text-center">
          <p className="text-sm text-red-500">笔记加载失败</p>
          <p className="mt-1 text-xs text-gray-400">{(error as Error)?.message ?? "请稍后重试"}</p>
        </div>
      </div>
    );
  }

  const question = note.question;

  return (
    <div className="min-h-screen bg-white pb-10">
      <Header onBack={() => navigate(-1)} />

      <main>
        <section className="px-4 pb-7 pt-4">
          {question ? (
            <QuestionRenderer
              question={question}
              disabled
              showResult
              isCorrect
              correctAnswer={question.correct_answer}
              explanation={question.explanation}
              fontSize="max"
              showUserAnswer
              userAnswerFallback="/"
              showExplanationPanel={false}
            />
          ) : (
            <div className="rounded-xl bg-gray-50 p-5 text-center text-gray-400">关联题目已不可用</div>
          )}
        </section>

        <section className="border-t-[10px] border-gray-100 px-4 pt-7">
          <div className="mb-8 flex items-center justify-between">
            <div className="w-28" />
            <div className="flex items-center gap-3 text-[22px] font-semibold text-gray-900">
              <span className="h-px w-8 bg-gray-200" />
              试题详解
              <span className="h-px w-8 bg-gray-200" />
            </div>
            <button
              type="button"
              className="rounded-full border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700"
            >
              ⓘ 试题纠错
            </button>
          </div>

          <div className="space-y-3">
            <h2 className="inline-block bg-gradient-to-t from-[#7b61ff]/35 from-45% to-transparent to-45% text-[22px] font-semibold text-gray-950">
              解析
            </h2>
            <p className="text-[17px] leading-8 text-gray-400 whitespace-pre-wrap">
              {question?.explanation || "暂无解析"}
            </p>
          </div>

          <div className="mt-7 border-t border-gray-100 pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="inline-block bg-gradient-to-t from-[#7b61ff]/35 from-45% to-transparent to-45% text-[22px] font-semibold text-gray-950">
                笔记
              </h2>
              <button
                type="button"
                onClick={() => setEditingNote(note)}
                className="rounded-full border border-[#6f55ff] px-5 py-1.5 text-[17px] font-semibold text-[#6f55ff]"
              >
                编辑笔记
              </button>
            </div>
            <p className="text-[20px] leading-9 text-gray-900 whitespace-pre-wrap">{note.content}</p>
          </div>
        </section>
      </main>

      {editingNote && (
        <NoteForm
          note={editingNote}
          onSubmit={(content) => updateMut.mutate({ id: editingNote.id, content })}
          onCancel={() => setEditingNote(null)}
          isSubmitting={updateMut.isPending}
        />
      )}
    </div>
  );
}

function Header({ onBack }: { onBack: () => void }) {
  return (
    <header className="sticky top-0 z-20 bg-white">
      <div className="relative flex h-16 items-center justify-center px-4">
        <button
          type="button"
          onClick={onBack}
          className="absolute left-3 flex h-10 w-10 items-center justify-center text-gray-900"
          aria-label="返回"
        >
          <ChevronLeftIcon />
        </button>
        <h1 className="text-[20px] font-semibold text-gray-950">查看笔记</h1>
        <div className="absolute right-3 flex h-10 items-center rounded-full border border-gray-200 bg-white px-3 text-gray-900">
          <span className="text-lg leading-none">•••</span>
          <span className="mx-3 h-5 w-px bg-gray-200" />
          <span className="h-5 w-5 rounded-full border-[5px] border-gray-900" />
        </div>
      </div>
    </header>
  );
}

function NoteForm({
  note,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  note: NoteDTO;
  onSubmit: (content: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [content, setContent] = useState(note.content);

  const handleSubmit = () => {
    if (!content.trim()) return;
    onSubmit(content.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
      <div className="flex max-h-[70vh] w-full max-w-lg flex-col rounded-t-2xl bg-white p-4">
        <h3 className="mb-3 text-lg font-semibold">编辑笔记</h3>
        <textarea
          autoFocus
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="写下你的笔记..."
          className="min-h-[120px] flex-1 resize-none rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <div className="mt-3 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-200 py-2.5 font-medium text-gray-600"
          >
            取消
          </button>
          <button
            disabled={!content.trim() || isSubmitting}
            onClick={handleSubmit}
            className="flex-1 rounded-xl bg-primary py-2.5 font-medium text-white disabled:opacity-40"
          >
            {isSubmitting ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
